import { useState, useMemo, useEffect, useRef } from 'react'
import { useAuth } from '@/contexts/AuthProvider'
import { useMobileProfile } from '@/hooks/useMobileProfile'
import QualificationCard from '@/components/QualificationCard'
import { getDisplayStatus } from '@/lib/qualificationStatus'
import { Loader2, Search } from 'lucide-react'
import { cn } from '@/lib/utils'

const filters = [
  { key: 'all', label: 'All' },
  { key: 'completed', label: 'Completed' },
  { key: 'expired', label: 'Expired' },
  { key: 'not_started', label: 'Not Started' },
]

export default function TrainingPage() {
  const { userProfile, company } = useAuth()
  const { qualifications, complianceStats, loading, refetch } = useMobileProfile(userProfile?.id, company?.id)
  const [activeFilter, setActiveFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceRef = useRef(null)

  // Debounce search input
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [search])

  // Memoize filter counts
  const counts = useMemo(() => ({
    all: qualifications.length,
    completed: qualifications.filter(q => getDisplayStatus(q) === 'completed').length,
    expired: qualifications.filter(q => getDisplayStatus(q) === 'expired').length,
    not_started: qualifications.filter(q => {
      const ds = getDisplayStatus(q)
      return ds === 'not_started' || ds === 'not_evidenced' || ds === 'pending'
    }).length,
  }), [qualifications])

  // Memoize filtered list
  const filtered = useMemo(() => {
    return qualifications.filter(q => {
      if (activeFilter !== 'all') {
        const ds = getDisplayStatus(q)
        if (activeFilter === 'completed' && ds !== 'completed') return false
        if (activeFilter === 'expired' && ds !== 'expired') return false
        if (activeFilter === 'not_started' && ds !== 'not_started' && ds !== 'not_evidenced' && ds !== 'pending') return false
      }
      if (debouncedSearch) {
        const s = debouncedSearch.toLowerCase()
        const name = (q.requirement_name || '').toLowerCase()
        const category = (q.requirement_category || '').toLowerCase()
        const spaceNames = (q.spaces || []).map(sp => sp.name.toLowerCase()).join(' ')
        if (!name.includes(s) && !category.includes(s) && !spaceNames.includes(s)) return false
      }
      return true
    })
  }, [qualifications, activeFilter, debouncedSearch])

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">My Training</h1>
        <p className="text-sm text-gray-500">
          {complianceStats.compliance_percentage}% compliant — {complianceStats.compliant} of {complianceStats.total} compliant
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search qualifications..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        />
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setActiveFilter(f.key)}
            className={cn(
              'flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
              activeFilter === f.key
                ? 'bg-emerald-500 text-white'
                : 'bg-gray-100 text-gray-600 active:bg-gray-200'
            )}
          >
            {f.label} ({counts[f.key]})
          </button>
        ))}
      </div>

      {/* Qualification list */}
      {qualifications.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm space-y-1">
          <p className="font-medium text-gray-500">No qualifications assigned yet</p>
          <p>You'll see your training requirements here once your manager assigns you to a space.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          No qualifications match your filter.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(q => (
            <QualificationCard key={q.id} qualification={q} />
          ))}
        </div>
      )}
    </div>
  )
}
